/**
 * Beta Disclaimer Gate
 * --------------------
 * Hard gate before /signup. Shown ONLY on the create-account path
 * (not on login). Requires explicit checkbox acceptance before
 * proceeding to /signup. Once accepted, sessionStorage flag is set
 * so the user isn't re-prompted on the same browser session.
 *
 * Companion legal page: /beta-terms (the full plain-English version)
 * — but the bullets here are the canonical short list shown at the gate.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, ArrowLeft, ShieldAlert } from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import MarketingScrollToTop from "@/components/MarketingScrollToTop";

export const BETA_ACCEPT_KEY = "bloq_beta_accepted_v1";

export default function BetaDisclaimer() {
  const navigate = useNavigate();
  const [agree, setAgree] = useState(false);

  const onContinue = () => {
    if (!agree) return;
    try {
      sessionStorage.setItem(BETA_ACCEPT_KEY, "1");
    } catch {
      // sessionStorage might be unavailable (Safari private mode, etc.) —
      // the signup form will catch any unchecked boxes as a fallback.
    }
    navigate("/signup");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fbfcfc]" data-testid="beta-disclaimer-page">
      <MarketingScrollToTop />
      <div className="bloq-container pt-7">
        <Link to="/" aria-label="Bloq home"><BloqLogo size={26} /></Link>
      </div>

      <div className="flex-1 bloq-container py-12 max-w-3xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-4">
          <span
            className="inline-flex items-center justify-center w-10 h-10 bg-[#b45309] text-white"
            aria-hidden
          >
            <ShieldAlert className="w-5 h-5" />
          </span>
          <p
            className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#b45309]"
            data-testid="beta-disclaimer-eyebrow"
          >
            Beta · please read before creating an account
          </p>
        </div>

        <h1
          className="bloq-display text-4xl sm:text-5xl mb-4"
          data-testid="beta-disclaimer-headline"
        >
          Bloq is in <span className="bloq-gradient-text">beta.</span>
        </h1>
        <p className="text-[#4a4a52] text-base leading-relaxed mb-8">
          We're building Bloq in the open. That means real features ship every week —
          and that also means things will occasionally break, change, or disappear without
          notice. Before you create an account, please understand what that means for you.
        </p>

        <div
          className="bloq-card p-6 sm:p-8 mb-6 border-l-4"
          style={{ borderLeftColor: "#b45309" }}
          data-testid="beta-disclaimer-card"
        >
          <p className="text-sm font-semibold text-[#1a1a1f] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#b45309]" /> What you need to know
          </p>
          <ul className="flex flex-col gap-3 text-sm text-[#1a1a1f] leading-relaxed">
            <li>
              <strong>This product is not officially launched.</strong> Bloq is in active beta.
              Features are incomplete, may behave unexpectedly, and could change or be
              removed at any time without notice.
            </li>
            <li>
              <strong>No guarantees.</strong> Bloq, Inc. makes no representation or warranty
              that the platform will be available, reliable, performant, secure, error-free,
              or fit for any particular purpose during the beta period.
            </li>
            <li>
              <strong>Access may be revoked.</strong> We may suspend, throttle, or terminate
              your access — or the entire service — at any time, for any reason, with or
              without notice. No refunds are guaranteed during beta.
            </li>
            <li>
              <strong>Don't store anything critical.</strong> Do not rely on Bloq as the
              system of record for business-critical, regulated, legally-protected, or
              irreplaceable data. Keep your own backups. Data loss can and will happen.
            </li>
            <li>
              <strong>Use at your own risk.</strong> You assume full responsibility for any
              consequences of using the platform — including meetings, recordings, summaries,
              transcripts, bookings, emails, integrations, follow-ups, payments, and notes.
            </li>
            <li>
              <strong>No liability.</strong> To the maximum extent allowed by law, Bloq, Inc.
              and its officers, directors, employees, and contractors will not be liable for
              any direct, indirect, incidental, special, consequential, punitive, or exemplary
              damages — including lost revenue, lost profits, lost data, business interruption,
              reputational harm, or replacement costs — arising from your use of the beta.
            </li>
            <li>
              <strong>No professional advice.</strong> Bloq is a workflow tool, not a
              substitute for legal, financial, medical, accounting, or other professional
              advice. AI-generated summaries, transcripts, follow-ups, and suggestions may
              contain errors. Verify before acting.
            </li>
            <li>
              <strong>Third-party services.</strong> Bloq integrates with calendars, payment
              processors, email providers, and other third-party services. Their availability,
              accuracy, and security are outside our control.
            </li>
            <li>
              <strong>Governing law.</strong> Bloq, Inc. is a Colorado corporation. Any
              dispute arising from your use of the beta is governed by the laws of the State
              of Colorado, USA. Full terms are in our{" "}
              <Link to="/terms" className="text-[#005e7a] underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#005e7a] underline">
                Privacy Policy
              </Link>.
            </li>
            <li>
              <strong>Beta feedback.</strong> If something breaks, please tell us — email{" "}
              <a
                href="mailto:support@bloq.video"
                className="text-[#005e7a] underline"
                data-testid="beta-disclaimer-support-email"
              >
                support@bloq.video
              </a>
              . We can't fix what we don't know about.
            </li>
          </ul>
        </div>

        <label
          className="flex items-start gap-3 cursor-pointer select-none mb-6 bloq-card p-4"
          data-testid="beta-disclaimer-agree-row"
        >
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#005e7a]"
            data-testid="beta-disclaimer-agree-checkbox"
          />
          <span className="text-sm text-[#1a1a1f] leading-relaxed">
            I've read and understand the beta disclaimer above. I accept that Bloq is
            unfinished, may break, may revoke my access at any time, and is provided
            on an as-is, use-at-your-own-risk basis. I agree not to store
            business-critical or irreplaceable data on the platform during beta.
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onContinue}
            disabled={!agree}
            className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 font-medium transition-opacity ${
              agree
                ? "bloq-btn-primary"
                : "bg-[#4a4a52]/20 text-[#4a4a52] cursor-not-allowed"
            }`}
            data-testid="beta-disclaimer-continue-button"
          >
            Continue to sign up
            <ArrowRight className="w-4 h-4" />
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium text-[#4a4a52] hover:text-[#1a1a1f]"
            data-testid="beta-disclaimer-cancel-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Cancel — back to homepage
          </Link>
        </div>

        <p className="mt-8 text-xs text-[#82828c]">
          Already on Bloq?{" "}
          <Link
            to="/login"
            className="text-[#005e7a] underline"
            data-testid="beta-disclaimer-login-link"
          >
            Log in instead
          </Link>{" "}
          — existing accounts skip this page.
        </p>
      </div>
    </div>
  );
}
